"use client"

import { useState, useEffect, useMemo } from "react"
import {
  FileText,
  DollarSign,
  UserCheck,
  Target,
  X,
  AlertTriangle,
  UserPlus,
  Building2,
  CheckCircle,
  FolderOpen,
  AlertCircle,
  Users,
  ClipboardCheck,
  BarChart3,
  GitBranch,
  ShieldOff,
} from "lucide-react"
import { Layout } from "@/shared/components"
import {
  SalesReportManager,
  PayReportManager,
  CommissionRateManager,
  OverrideRateManager,
  AgentManagement as AgentStatusManager,
  AgentAssignmentManager,
  OrganizationManager,
  RecruitTrackingPanel,
  OrganizationTreeView,
  AgentDeactivationManager,
  RecruitApplicationDetail,
} from "@/features/hr"
import { ReferralLinkCard } from "@/features/registration/components/ReferralLinkCard"
import { AgentCampaignSelector } from "@/features/agents"
import { useAuth } from "@/features/auth"
import { useCampaign } from "@/features/campaigns"
import { checkHRPermission, TAB_TO_FEATURE_MAP } from "@/features/hr/utils/hr-permissions"
import HRAccountManagement from "@/features/hr/components/HRAccountManagement"
import { QualityControl } from "@/features/quality-control"
import { OnboardingFunnel } from "@/features/referral-analytics/components/OnboardingFunnel"
import { ApplicationStatusFunnel } from "@/features/agents/components/ApplicationStatusFunnel"
import EnrollmentList from "@/features/enrollment/components/EnrollmentList"
import { Clock } from "lucide-react"

interface HRManagerDashboardProps {
  onError: (error: string) => void
}

export default function HRManagerDashboard({ onError }: HRManagerDashboardProps) {
  const { getHRManagerOrganizationId, user } = useAuth()
  const { selectedCampaign, setSelectedCampaign, isCampaignSelected } = useCampaign()

  // All possible navigation items
  const allNavigationItems = [
    { id: "campaign-selector", label: "Campaign Selector", icon: FolderOpen },
    { id: "hr-account-management", label: "HR Account Management", icon: Users },
    { id: "referrals", label: "Referrals", icon: UserPlus },
    { id: "recruit-tracking", label: "Recruit Tracking", icon: BarChart3 },
    { id: "organization-tree", label: "Organization Tree", icon: GitBranch },
    { id: "sales-reports", label: "Sales Reports", icon: FileText },
    { id: "pay-reports", label: "Pay Reports", icon: DollarSign },
    { id: "agent-assignment", label: "Agent Assignment", icon: UserPlus },
    { id: "commission-rates", label: "Commission Rates", icon: Target },
    { id: "override-rates", label: "Override Rates", icon: Target },
    { id: "agent-status", label: "Agent Status", icon: UserCheck },
    { id: "agent-deactivation", label: "Agent Deactivation", icon: ShieldOff },
    { id: "organizations", label: "Organizations", icon: Building2 },
    { id: "quality-control", label: "Quality Control", icon: ClipboardCheck },
    { id: "in-progress-applications", label: "In Progress Applications", icon: Clock },
    { id: "submitted-applications", label: "Submitted Applications", icon: CheckCircle },
    { id: "application-funnel", label: "Application Status Funnel", icon: Target },
  ]

  // Filter navigation items based on HR Manager permissions
  const navigationItems = useMemo(() => {
    // Debug logging
    console.log('[HR Dashboard] User:', {
      role: user?.role,
      hrManagerType: user?.hrManagerType,
      hrManagerTypeType: typeof user?.hrManagerType
    })

    // FMO CEO and Admin get all tabs
    if (user?.role === 'fmo_ceo' || user?.role === 'admin') {
      console.log('[HR Dashboard] FMO/Admin - showing all tabs')
      return allNavigationItems
    }

    // For HR Managers, filter based on hrManagerType
    if (user?.role === 'hr_manager') {
      const hrManagerType = user.hrManagerType
      console.log('[HR Dashboard] HR Manager Type:', hrManagerType)

      // If no hrManagerType is set, show all tabs except HR Account Management (backward compatibility)
      // This allows HR managers to work before FMO assigns them a specific type
      if (!hrManagerType) {
        console.log('[HR Dashboard] No type assigned - showing all tabs except HR Account Management')
        return allNavigationItems.filter(item => item.id !== 'hr-account-management')
      }

      // Filter tabs based on assigned HR Manager type permissions
      const filtered = allNavigationItems.filter(item => {
        // Campaign selector is always available
        if (item.id === 'campaign-selector') {
          return true
        }

        // HR Account Management is FMO-only
        if (item.id === 'hr-account-management') {
          return false
        }

        // Check if this tab requires a permission
        const feature = TAB_TO_FEATURE_MAP[item.id]
        if (!feature) {
          return true // If no feature mapping, allow by default
        }

        // Check permission based on HR Manager type
        const hasPermission = checkHRPermission(hrManagerType, feature)
        console.log(`[HR Dashboard] ${item.id} -> ${feature}: ${hasPermission}`)
        return hasPermission
      })

      console.log('[HR Dashboard] Filtered tabs:', filtered.map(i => i.id))
      return filtered
    }

    // Default: show all tabs for other roles
    return allNavigationItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.hrManagerType])

  // Get initial tab from URL hash or default to campaign-selector
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabIds = navigationItems.map(item => item.id)
    return validTabIds.includes(hash) ? hash : "campaign-selector"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // HR Staff feature states
  const [selectedRecruitId, setSelectedRecruitId] = useState<string | null>(null)
  const [showRecruitDetail, setShowRecruitDetail] = useState(false)

  // Update active tab if current tab becomes unavailable due to permission changes
  useEffect(() => {
    const validTabIds = navigationItems.map(item => item.id)
    if (!validTabIds.includes(activeTab)) {
      setActiveTab("campaign-selector")
    }
  }, [navigationItems, activeTab])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }

  const handleError = (errorMessage: string) => {
    setError(errorMessage)
    onError(errorMessage)
    setTimeout(() => setError(null), 5000)
  }

  const handleSuccess = (successMessage: string) => {
    setSuccess(successMessage)
    setTimeout(() => setSuccess(null), 5000)
  }

  // Campaign selection requirement component
  const CampaignSelectionRequired = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center max-w-md">
        <div className="bg-yellow-50 border border-yellow-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-yellow-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Selection Required</h3>
        <p className="text-gray-600 mb-4">
          Please select a campaign from the Campaign Selector tab to view and manage campaign-specific HR data.
        </p>
        <button
          onClick={() => handleTabChange("campaign-selector")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go to Campaign Selector
        </button>
      </div>
    </div>
  )

  const renderContent = () => {
    // Campaign selector is always accessible
    if (activeTab === "campaign-selector") {
      return (
        <AgentCampaignSelector
          selectedCampaignId={selectedCampaign?.id}
          onCampaignSelect={(campaign) => setSelectedCampaign(campaign)}
          showActions={false}
          organizationId={getHRManagerOrganizationId()}
        />
      )
    }

    // HR Account Management is accessible without campaign selection (FMO only)
    if (activeTab === "hr-account-management") {
      return <HRAccountManagement onError={handleError} onSuccess={handleSuccess} />
    }

    // Referrals is accessible without campaign selection
    if (activeTab === "referrals") {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite New Recruits</h2>
            <p className="text-gray-600">Share your referral link to grow your team and earn commissions</p>
          </div>
          <ReferralLinkCard />
        </div>
      )
    }

    // Recruit Tracking is accessible without campaign selection (HR Staff feature)
    if (activeTab === "recruit-tracking") {
      return (
        <div className="space-y-6">
          <RecruitTrackingPanel
            onError={handleError}
            onViewRecruit={(recruitId) => {
              setSelectedRecruitId(recruitId)
              setShowRecruitDetail(true)
            }}
          />

          {/* Onboarding Funnel - Shows organization-wide metrics */}
          <div className="mt-8">
            <OnboardingFunnel />
          </div>
        </div>
      )
    }

    // Organization Tree is accessible without campaign selection (HR Staff feature)
    if (activeTab === "organization-tree") {
      return <OrganizationTreeView onError={handleError} />
    }

    // Agent Deactivation management (HR Staff feature - requires agent selection)
    if (activeTab === "agent-deactivation") {
      return (
        <AgentDeactivationSelector
          onError={handleError}
          onSuccess={handleSuccess}
        />
      )
    }

    // All other tabs require campaign selection
    if (!isCampaignSelected) {
      return <CampaignSelectionRequired />
    }

    const commonProps = {
      onError: handleError,
      selectedCampaign: selectedCampaign?.id,
      organizationId: getHRManagerOrganizationId()
    }

    switch (activeTab) {
      case "sales-reports":
        return <SalesReportManager {...commonProps} />
      case "pay-reports":
        return <PayReportManager {...commonProps} />
      case "agent-assignment":
        return <AgentAssignmentManager {...commonProps} />
      case "commission-rates":
        return <CommissionRateManager {...commonProps} />
      case "override-rates":
        return <OverrideRateManager {...commonProps} />
      case "agent-status":
        return <AgentStatusManager {...commonProps} />
      case "organizations":
        return <OrganizationManager onError={handleError} onSuccess={handleSuccess} organizationId={getHRManagerOrganizationId()} />
      case "quality-control":
        return <QualityControl campaignId={selectedCampaign?.id} campaignName={selectedCampaign?.name} />
      case "in-progress-applications":
        return <EnrollmentList status="IN_PROGRESS" campaignId={selectedCampaign?.id} campaignName={selectedCampaign?.name} />
      case "submitted-applications":
        return <EnrollmentList status="SUBMITTED" campaignId={selectedCampaign?.id} campaignName={selectedCampaign?.name} />
      case "application-funnel":
        return <ApplicationStatusFunnel campaignId={selectedCampaign?.id} />
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="HR Manager Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-2 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-2 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
          ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>

          {/* Campaign Status */}
          {isCampaignSelected && (
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Active Campaign</span>
              </div>
              <p className="text-sm text-emerald-700 font-semibold mb-3 truncate">
                {selectedCampaign?.name}
              </p>
              <button
                onClick={() => {
                  setSelectedCampaign(null)
                  handleTabChange('campaign-selector')
                }}
                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}

          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isDisabled = !['campaign-selector', 'hr-account-management', 'referrals', 'recruit-tracking', 'organization-tree', 'agent-deactivation'].includes(item.id) && !isCampaignSelected
              const isActive = activeTab === item.id

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) {
                      handleTabChange('campaign-selector')
                    } else {
                      handleTabChange(item.id)
                    }
                    setMobileSidebarOpen(false)
                  }}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${isActive
                    ? "bg-blue-600 text-white shadow-md"
                    : isDisabled
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : isDisabled ? "text-slate-400" : "text-slate-500"
                    }`} />
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">
          {renderContent()}
        </div>
      </div>

      {/* Recruit Detail Modal */}
      {showRecruitDetail && selectedRecruitId && (
        <RecruitApplicationDetail
          recruitId={selectedRecruitId}
          onError={handleError}
          onClose={() => {
            setShowRecruitDetail(false)
            setSelectedRecruitId(null)
          }}
        />
      )}
    </Layout>
  )
}

// Agent Deactivation Selector Component (inline for simplicity)
function AgentDeactivationSelector({
  onError,
  onSuccess,
}: {
  onError: (error: string) => void
  onSuccess: (message: string) => void
}) {
  const [agents, setAgents] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null)
  const { HRService } = require('@/features/hr')

  const loadAgents = async () => {
    try {
      setLoading(true)
      const result = await HRService.getAgents({ search: searchTerm || undefined })
      setAgents(result || [])
    } catch (error: any) {
      console.error('Error loading agents:', error)
      onError(error.message || 'Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAgents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadAgents()
  }

  if (selectedAgent) {
    return (
      <AgentDeactivationManager
        agentId={selectedAgent.id}
        agentName={`${selectedAgent.user?.firstName || ''} ${selectedAgent.user?.lastName || ''}`}
        onError={onError}
        onSuccess={onSuccess}
        onClose={() => setSelectedAgent(null)}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ShieldOff className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Agent Deactivation Manager</h2>
            <p className="text-sm text-gray-500">Manage agent status with granular controls</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search agents by name or email..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Search
            </button>
          </div>
        </form>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="py-4 flex items-center justify-between hover:bg-gray-50 px-2 rounded-lg cursor-pointer"
                onClick={() => setSelectedAgent(agent)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                    {agent.user?.firstName?.[0]}{agent.user?.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {agent.user?.firstName} {agent.user?.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{agent.user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${agent.status === 'active' ? 'bg-green-100 text-green-800' :
                    agent.status === 'deactivated' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                    {agent.status}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${agent.canSell !== false ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                    {agent.canSell !== false ? 'Can Sell' : 'Cannot Sell'}
                  </span>
                </div>
              </div>
            ))}
            {agents.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No agents found. Try a different search term.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
